/**
 * Stone Investment Project Detail Content
 * 
 * Luxury brochure-style project details with Stone Investment's editorial aesthetic:
 * - Overview with key detail cards
 * - Unit availability with progress bar
 * - Media gallery with expand toggle
 * - Project timeline
 * - Contact CTA
 */

'use client';

import { MarkdownRenderer } from '@routeroyal/ui-core';
import Image from 'next/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  CheckCircle2,
  Home,
  Mail,
  Phone,
  Globe,
  ArrowRight,
  ChevronRight,
  Download,
  MapPin,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

const MapComponent = dynamic(
  () => import('../../../../src/components/core/MapComponent').then(mod => mod.MapComponent),
  { ssr: false }
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UnitMixEntry {
  propertyTypeName: string;
  count: number;
}

interface ProjectData {
  id: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  projectType?: string | null;
  status?: string | null;
  stage?: string | null;
  totalUnits?: number | null;
  soldUnits?: number | null;
  availableUnits?: number | null;
  unitMixSummary?: string | null;
  unitMixDetails?: UnitMixEntry[] | Record<string, unknown> | null;
  displayPriceText?: string | null;
  heroImageUrl?: string | null;
  projectWebsiteUrl?: string | null;
  marketingStatusText?: string | null;
  estimatedStartDate?: string | null;
  estimatedCompletionDate?: string | null;
  developmentApprovalDate?: string | null;
  developmentApprovalStatus?: string | null;
}

interface MediaItem {
  id: string;
  mediaId?: string;
  usageContext?: string;
  sortOrder?: number;
  contextualTitle?: string | null;
  media: {
    id: string;
    url: string;
    thumbnailUrl?: string;
    filename?: string;
    originalFilename?: string;
    mimeType?: string;
    title?: string;
  };
}

/** Simplified listing card data for development unit display */
interface DevelopmentListingCard {
  id: string;
  listingId: string;
  property: {
    id: string;
    name: string;
    description: string | null;
    bedrooms: number | null;
    bathrooms: number | null;
    totalFloorArea: number | null;
    totalFloorAreaUnit: string | null;
    primaryImageUrl: string | null;
    currency: string;
  };
  listingType: string;
  status: string;
  displayPriceText: string | null;
  mainHeadline: string | null;
  price: {
    amount: number;
    currency: string;
    frequency?: string | null;
  } | null;
  propertyType?: {
    id: string;
    name: string;
    icon: string | null;
  } | null;
}

/** Location data for map display */
interface LocationData {
  latitude?: number | null;
  longitude?: number | null;
  formattedAddress?: string | null;
  city?: string | null;
  suburb?: string | null;
  state?: string | null;
}

export interface StoneProjectDetailContentProps {
  /** Full project data */
  project: ProjectData;
  /** Project media items */
  media?: MediaItem[];
  /** Translation messages */
  messages?: Record<string, string>;
  /** Current locale */
  locale?: string;
  /** Contact phone number */
  contactPhone?: string;
  /** Contact email address */
  contactEmail?: string;
  /** Custom className */
  className?: string;
  /** Listings (units) that belong to this development */
  developmentListings?: DevelopmentListingCard[];
  /** Base path for building property links */
  basePath?: string;
  /** Development location for map display */
  location?: LocationData;
  /** URL to a downloadable brochure PDF */
  brochureUrl?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SERIF = "'Cormorant Garamond', Georgia, serif";

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-6 flex items-center gap-4">
      <span className="h-px w-10 bg-stone-400" />
      <span className="text-[10px] font-sans font-medium uppercase tracking-[0.25em] text-stone-500">
        {children}
      </span>
      <span className="h-px w-10 bg-stone-400" />
    </div>
  );
}

function formatStage(stage: string | null | undefined, messages?: Record<string, string>): string {
  if (!stage) return '';
  if (messages) {
    const stageMap: Record<string, string> = {
      UNDER_CONSTRUCTION: messages.stageUnderConstruction || '',
      PLANNING: messages.stageInPlanning || '',
      COMPLETED: messages.stageCompleted || '',
      SELLING: messages.stageNowSelling || '',
    };
    if (stageMap[stage]) return stageMap[stage];
  }
  return stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function parseUnitMixDetails(
  raw: UnitMixEntry[] | Record<string, unknown> | null | undefined,
): UnitMixEntry[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter(
      (e): e is UnitMixEntry =>
        typeof e === 'object' && e !== null && 'propertyTypeName' in e && 'count' in e,
    );
  }
  return [];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StoneProjectDetailContent({
  project,
  media = [],
  messages = {},
  locale = 'en',
  contactPhone,
  contactEmail,
  className = '',
  developmentListings = [],
  basePath = '',
  location,
  brochureUrl,
}: StoneProjectDetailContentProps) {
  const [showAllGallery, setShowAllGallery] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [unitTypeFilter, setUnitTypeFilter] = useState<string | null>(null);

  const unitPropertyTypes = useMemo(() => {
    const types = new Map<string, string>();
    for (const listing of developmentListings) {
      if (listing.propertyType?.name) {
        types.set(listing.propertyType.id, listing.propertyType.name);
      }
    }
    return Array.from(types.entries())
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, name]) => ({ id, name }));
  }, [developmentListings]);

  const filteredListings = useMemo(() => {
    if (!unitTypeFilter) return developmentListings;
    return developmentListings.filter(l => l.propertyType?.id === unitTypeFilter);
  }, [developmentListings, unitTypeFilter]);

  const galleryImages = media.filter(
    m => m.usageContext === 'GALLERY' || m.usageContext === 'PRIMARY_HERO' || m.usageContext === 'AERIAL'
  );
  const displayedImages = showAllGallery ? galleryImages : galleryImages.slice(0, 6);

  const hasTimeline =
    project.developmentApprovalDate || project.estimatedStartDate || project.estimatedCompletionDate;

  return (
    <div className={`bg-white ${className}`}>
      {/* ================================================================= */}
      {/* OVERVIEW SECTION                                                  */}
      {/* ================================================================= */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
            {/* Left Column – Description */}
            <div className="lg:col-span-3">
              <SectionLabel>{messages.overview || 'Overview'}</SectionLabel>
              <h2
                className="text-3xl lg:text-4xl text-stone-900 mb-8 leading-tight"
                style={{ fontFamily: SERIF }}
              >
                {messages.aboutProject || 'About the Development'}
              </h2>

              {project.description && (
                <MarkdownRenderer
                  content={project.description}
                  theme="stone"
                  className="font-sans text-stone-700 leading-relaxed"
                />
              )}

              {project.marketingStatusText && (
                <div className="mt-8 inline-flex items-center gap-2 border border-stone-300 px-5 py-2.5 text-xs font-sans font-medium uppercase tracking-[0.15em] text-stone-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-stone-500" />
                  {project.marketingStatusText}
                </div>
              )}

              {brochureUrl && (
                <div className="mt-8">
                  <a
                    href={brochureUrl}
                    download
                    className="group inline-flex items-center gap-3 bg-stone-900 px-6 py-3.5 text-sm font-sans font-medium uppercase tracking-widest text-white hover:bg-stone-800 transition-colors"
                  >
                    <Download size={16} strokeWidth={1.5} />
                    {messages.downloadBrochure || 'Download Brochure'}
                    <ArrowRight
                      size={14}
                      strokeWidth={1.5}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </a>
                </div>
              )}
            </div>

            {/* Right Column – Key Details */}
            <div className="lg:col-span-2 space-y-4">
              <p
                className="text-xs font-sans font-medium uppercase tracking-[0.2em] text-stone-400 mb-6"
              >
                {messages.projectDetails || 'Key Details'}
              </p>

              {project.projectType && (
                <DetailCard
                  label={messages.projectType || 'Project Type'}
                  value={project.projectType.replace(/_/g, ' ')}
                />
              )}

              {/* Unit Mix Breakdown */}
              {(() => {
                const entries = parseUnitMixDetails(project.unitMixDetails);
                if (entries.length > 0) {
                  return (
                    <div className="border border-stone-200 bg-stone-50 p-5 transition-colors hover:bg-stone-100">
                      <p className="text-[10px] font-sans uppercase tracking-[0.2em] text-stone-400 mb-4">
                        {messages.unitTypes || 'Unit Types'}
                      </p>
                      <div className="flex flex-wrap items-center gap-3">
                        {entries.map((entry, i) => (
                          <span key={entry.propertyTypeName} className="flex items-center gap-3">
                            {i > 0 && <span className="h-4 w-px bg-stone-300" />}
                            <span className="font-sans text-lg font-medium text-stone-800">
                              {entry.propertyTypeName}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                }
                if (project.unitMixSummary) {
                  return (
                    <DetailCard
                      label={messages.unitMix || 'Unit Mix'}
                      value={project.unitMixSummary}
                    />
                  );
                }
                return null;
              })()}

              {/* Total Units / Available Units - Hidden per client request */}

              {project.estimatedCompletionDate && (
                <DetailCard
                  label={messages.estimatedCompletion || 'Estimated Completion'}
                  value={new Date(project.estimatedCompletionDate).toLocaleDateString(locale, {
                    month: 'long',
                    year: 'numeric',
                  })}
                />
              )}

              {project.stage && (
                <DetailCard
                  label={messages.projectStage || 'Current Stage'}
                  value={formatStage(project.stage, messages)}
                />
              )}

              {project.displayPriceText && (
                <DetailCard
                  label={messages.pricing || 'Pricing'}
                  value={project.displayPriceText}
                  highlight
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Unit Availability Section - Hidden per client request */}

      {/* ================================================================= */}
      {/* DEVELOPMENT UNITS SECTION                                         */}
      {/* ================================================================= */}
      {developmentListings.length > 0 && (
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <SectionLabel>{messages.availableUnits || 'Available Units'}</SectionLabel>
            <h2 className="mb-8 text-4xl text-stone-900 md:text-5xl" style={{ fontFamily: SERIF }}>
              {messages.properties || 'Properties'}
            </h2>

            {unitPropertyTypes.length > 1 && (
              <div className="mb-12 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setUnitTypeFilter(null)}
                  className={`rounded-full border px-5 py-2 font-sans text-xs font-medium uppercase tracking-[0.15em] transition-colors ${
                    !unitTypeFilter
                      ? 'border-stone-900 bg-stone-900 text-white'
                      : 'border-stone-300 text-stone-600 hover:border-stone-500'
                  }`}
                >
                  {messages.all || 'All'} ({developmentListings.length})
                </button>
                {unitPropertyTypes.map(pt => {
                  const count = developmentListings.filter(l => l.propertyType?.id === pt.id).length;
                  return (
                    <button
                      key={pt.id}
                      onClick={() => setUnitTypeFilter(unitTypeFilter === pt.id ? null : pt.id)}
                      className={`rounded-full border px-5 py-2 font-sans text-xs font-medium uppercase tracking-[0.15em] transition-colors ${
                        unitTypeFilter === pt.id
                          ? 'border-stone-900 bg-stone-900 text-white'
                          : 'border-stone-300 text-stone-600 hover:border-stone-500'
                      }`}
                    >
                      {pt.name} ({count})
                    </button>
                  );
                })}
              </div>
            )}

            <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
              {filteredListings.map((listing) => (
                <Link
                  key={listing.id}
                  href={`${basePath}/properties/${listing.property.id}`}
                  className="group block"
                >
                  <div className="relative aspect-4/3 overflow-hidden bg-stone-200">
                    {listing.property.primaryImageUrl ? (
                      <Image
                        src={listing.property.primaryImageUrl}
                        alt={listing.property.name}
                        fill
                        className="object-cover transition-transform duration-2000 ease-out group-hover:scale-105"
                        sizes="(max-width: 768px) 100vw, 50vw"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-stone-200">
                        <Home size={48} className="text-stone-400" strokeWidth={1} />
                      </div>
                    )}
                    {listing.status === 'SOLD' && (
                      <div className="absolute right-4 top-4 bg-stone-900 px-3 py-1.5 text-[10px] font-sans uppercase tracking-[0.2em] text-white">
                        {messages.sold || 'Sold'}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-4 pt-6">
                    <div className="flex items-center gap-3">
                      <h3 className="text-2xl text-stone-900 transition-all duration-500 group-hover:italic md:text-3xl" style={{ fontFamily: SERIF }}>
                        {listing.mainHeadline || listing.property.name}
                      </h3>
                      {listing.propertyType?.name && (
                        <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 font-sans text-[10px] font-medium uppercase tracking-[0.15em] text-stone-500">
                          {listing.propertyType.name}
                        </span>
                      )}
                    </div>
                    {(listing.displayPriceText || listing.price) && (
                      <p className="text-xl text-stone-900" style={{ fontFamily: SERIF }}>
                        {listing.displayPriceText || (listing.price
                          ? new Intl.NumberFormat(locale, {
                              style: 'currency',
                              currency: listing.price.currency,
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(listing.price.amount)
                          : (messages.priceOnRequest || 'Price on request'))}
                      </p>
                    )}
                    <div className="border-t border-stone-200" />
                    <div className="flex flex-wrap items-center gap-3 font-sans text-sm tracking-wider text-stone-600">
                      {listing.property.bedrooms != null && listing.property.bedrooms > 0 && (
                        <span>{listing.property.bedrooms} {messages.bed || 'bed'}</span>
                      )}
                      {listing.property.bathrooms != null && listing.property.bathrooms > 0 && (
                        <>
                          <span className="h-3 w-px bg-stone-300" />
                          <span>{listing.property.bathrooms} {messages.bath || 'bath'}</span>
                        </>
                      )}
                      {listing.property.totalFloorArea != null && listing.property.totalFloorArea > 0 && (
                        <>
                          <span className="h-3 w-px bg-stone-300" />
                          <span>{listing.property.totalFloorArea.toLocaleString()} {listing.property.totalFloorAreaUnit?.toLowerCase() || 'm²'}</span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================================================================= */}
      {/* GALLERY SECTION                                                   */}
      {/* ================================================================= */}
      {galleryImages.length > 0 && (
        <section className="py-20 lg:py-28 bg-stone-50">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            {/* Header */}
            <div className="flex items-end justify-between mb-12">
              <div>
                <SectionLabel>{messages.gallery || 'Gallery'}</SectionLabel>
                <h2
                  className="text-3xl lg:text-4xl text-stone-900"
                  style={{ fontFamily: SERIF }}
                >
                  {messages.projectGallery || 'Project Gallery'}
                </h2>
              </div>
              {galleryImages.length > 6 && (
                <button
                  onClick={() => setShowAllGallery(!showAllGallery)}
                  className="hidden sm:inline-flex items-center gap-2 text-sm font-sans font-medium text-stone-600 hover:text-stone-900 transition-colors"
                >
                  {showAllGallery
                    ? (messages.showLess || 'Show Less')
                    : (messages.viewAllPhotos || `View All ${galleryImages.length} Photos`).replace('{{count}}', String(galleryImages.length))}
                  <ChevronRight size={16} strokeWidth={1.5} />
                </button>
              )}
            </div>

            {/* Grid */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {displayedImages.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => setLightboxIndex(galleryImages.indexOf(item))}
                  className={`group relative overflow-hidden bg-stone-200 text-left ${
                    index === 0 ? 'sm:col-span-2 sm:row-span-2' : ''
                  }`}
                  style={{ aspectRatio: index === 0 ? '16/10' : '4/3' }}
                >
                  <Image
                    src={item.media.url}
                    alt={item.contextualTitle || project.name}
                    fill
                    className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    sizes={
                      index === 0
                        ? '(max-width: 768px) 100vw, 66vw'
                        : '(max-width: 768px) 100vw, 33vw'
                    }
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  {item.contextualTitle && (
                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                      <span className="text-sm font-sans text-white">
                        {item.contextualTitle}
                      </span>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Gallery Lightbox */}
            {lightboxIndex !== null && (
              <StoneGalleryLightbox
                images={galleryImages.map(item => ({
                  url: item.media.url,
                  thumbnailUrl: item.media.thumbnailUrl || item.media.url,
                  alt: item.contextualTitle || project.name,
                }))}
                initialIndex={lightboxIndex}
                projectName={project.name}
                onClose={() => setLightboxIndex(null)}
              />
            )}

            {/* Mobile expand button */}
            {galleryImages.length > 6 && (
              <button
                onClick={() => setShowAllGallery(!showAllGallery)}
                className="mt-8 w-full py-3 border border-stone-300 text-sm font-sans font-medium uppercase tracking-widest text-stone-600 hover:bg-stone-100 transition-colors sm:hidden"
              >
                {showAllGallery
                  ? (messages.showLess || 'Show Less')
                  : (messages.viewAllPhotos || `View All ${galleryImages.length} Photos`).replace('{{count}}', String(galleryImages.length))}
              </button>
            )}
          </div>
        </section>
      )}

      {/* ================================================================= */}
      {/* TIMELINE SECTION                                                  */}
      {/* ================================================================= */}
      {hasTimeline && (
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="text-center mb-16">
              <SectionLabel>{messages.timeline || 'Timeline'}</SectionLabel>
              <h2
                className="text-3xl lg:text-4xl text-stone-900"
                style={{ fontFamily: SERIF }}
              >
                {messages.projectTimeline || 'Project Timeline'}
              </h2>
            </div>

            <div className="mx-auto max-w-2xl">
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-5 top-0 bottom-0 w-px bg-stone-200" />

                <div className="space-y-10">
                  {project.developmentApprovalDate && (
                    <TimelineItem
                      icon={<CheckCircle2 size={18} strokeWidth={1.5} />}
                      label={messages.developmentApproval || 'Development Approval'}
                      date={new Date(project.developmentApprovalDate).toLocaleDateString(locale, {
                        month: 'long',
                        year: 'numeric',
                      })}
                      {...(project.developmentApprovalStatus ? { badge: project.developmentApprovalStatus } : {})}
                    />
                  )}

                  {project.estimatedStartDate && (
                    <TimelineItem
                      icon={<Calendar size={18} strokeWidth={1.5} />}
                      label={messages.constructionStart || 'Construction Start'}
                      date={new Date(project.estimatedStartDate).toLocaleDateString(locale, {
                        month: 'long',
                        year: 'numeric',
                      })}
                    />
                  )}

                  {project.estimatedCompletionDate && (
                    <TimelineItem
                      icon={<Home size={18} strokeWidth={1.5} />}
                      label={messages.estimatedCompletion || 'Estimated Completion'}
                      date={new Date(project.estimatedCompletionDate).toLocaleDateString(locale, {
                        month: 'long',
                        year: 'numeric',
                      })}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ================================================================= */}
      {/* LOCATION MAP SECTION                                              */}
      {/* ================================================================= */}
      {location?.latitude && location?.longitude && (
        <section className="py-20 lg:py-28 bg-stone-50">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="mb-12">
              <SectionLabel>{messages.location || 'Location'}</SectionLabel>
              <h2
                className="text-3xl lg:text-4xl text-stone-900"
                style={{ fontFamily: SERIF }}
              >
                {messages.projectLocation || 'Project Location'}
              </h2>
              {location.formattedAddress && (
                <p className="mt-4 flex items-center gap-2 font-sans text-stone-600">
                  <MapPin size={16} strokeWidth={1.5} className="text-stone-400" />
                  {location.formattedAddress}
                </p>
              )}
            </div>

            <div className="overflow-hidden">
              <MapComponent
                latitude={location.latitude}
                longitude={location.longitude}
                title={project.name}
                {...(location.formattedAddress ? { description: location.formattedAddress } : {})}
                height="450px"
                zoom={14}
                markerColor="#1c1917"
                enableDirections
                onGetDirections={() => {
                  window.open(
                    `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`,
                    '_blank'
                  );
                }}
              />
            </div>
          </div>
        </section>
      )}

      {/* ================================================================= */}
      {/* CTA SECTION                                                       */}
      {/* ================================================================= */}
      <section className="bg-stone-900 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12 text-center">
          <h2
            className="text-3xl lg:text-5xl text-white mb-6"
            style={{ fontFamily: SERIF }}
          >
            {(messages.interestedIn || 'Interested in {{name}}?').replace('{{name}}', project.name)}
          </h2>
          <p className="mx-auto max-w-2xl font-sans text-stone-400 leading-relaxed mb-10">
            {messages.registerInterestCta || 'Register your interest today and be the first to receive updates on availability, pricing, and exclusive offers.'}
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href={`/${locale}/contact?project=${project.slug || project.id}`}
              className="group inline-flex items-center gap-3 bg-white text-stone-900 px-8 py-4 text-sm font-sans font-medium uppercase tracking-widest hover:bg-stone-100 transition-colors"
            >
              {messages.registerInterest || 'Register Interest'}
              <ArrowRight
                size={16}
                strokeWidth={1.5}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>

            {brochureUrl && (
              <a
                href={brochureUrl}
                download
                className="group inline-flex items-center gap-3 border border-stone-400 text-white px-8 py-4 text-sm font-sans font-medium uppercase tracking-widest hover:border-white hover:bg-white/5 transition-colors"
              >
                <Download size={16} strokeWidth={1.5} />
                {messages.downloadBrochure || 'Download Brochure'}
              </a>
            )}

            {contactPhone && (
              <a
                href={`tel:${contactPhone}`}
                className="inline-flex items-center gap-3 border border-stone-600 text-white px-8 py-4 text-sm font-sans font-medium uppercase tracking-widest hover:border-stone-400 transition-colors"
              >
                <Phone size={16} strokeWidth={1.5} />
                {messages.callUs || 'Call Us'}
              </a>
            )}
          </div>

          {/* Contact details */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-sm font-sans text-stone-500">
            {contactPhone && (
              <a href={`tel:${contactPhone}`} className="flex items-center gap-2 hover:text-stone-300 transition-colors">
                <Phone size={14} strokeWidth={1.5} />
                {contactPhone}
              </a>
            )}
            {contactEmail && (
              <a href={`mailto:${contactEmail}`} className="flex items-center gap-2 hover:text-stone-300 transition-colors">
                <Mail size={14} strokeWidth={1.5} />
                {contactEmail}
              </a>
            )}
            {project.projectWebsiteUrl && (
              <a
                href={project.projectWebsiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-stone-300 transition-colors"
              >
                <Globe size={14} strokeWidth={1.5} />
                {messages.projectWebsite || 'Project Website'}
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DetailCard({
  label,
  value,
  highlight,
  accent,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="border border-stone-200 bg-stone-50 p-5 transition-colors hover:bg-stone-100">
      <p className="text-[10px] font-sans uppercase tracking-[0.2em] text-stone-400 mb-2">
        {label}
      </p>
      <p
        className={`font-sans text-lg font-medium ${
          accent
            ? 'text-emerald-700'
            : highlight
              ? 'text-stone-900 text-xl'
              : 'text-stone-800'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function TimelineItem({
  icon,
  label,
  date,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  date: string;
  badge?: string;
}) {
  return (
    <div className="relative flex gap-6 pl-0">
      <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center bg-white border border-stone-200 text-stone-600">
        {icon}
      </div>
      <div className="flex-1 pt-1">
        <p className="text-[10px] font-sans uppercase tracking-[0.2em] text-stone-400 mb-1">
          {label}
        </p>
        <p
          className="text-xl text-stone-900"
          style={{ fontFamily: SERIF }}
        >
          {date}
        </p>
        {badge && (
          <span className="mt-2 inline-block border border-stone-300 px-3 py-1 text-[10px] font-sans uppercase tracking-[0.15em] text-stone-500">
            {badge}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gallery Lightbox - Stone-themed fullscreen image viewer
// ---------------------------------------------------------------------------

interface LightboxImage {
  url: string;
  thumbnailUrl?: string;
  alt?: string;
}

function StoneGalleryLightbox({
  images,
  initialIndex,
  projectName,
  onClose,
}: {
  images: LightboxImage[];
  initialIndex: number;
  projectName: string;
  onClose: () => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  useEffect(() => {
    setIsZoomed(false);
  }, [currentIndex]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goToPrevious, goToNext]);

  const onTouchStartHandler = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX || null);
  };
  const onTouchMoveHandler = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0]?.clientX || null);
  };
  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) goToNext();
    if (distance < -50) goToPrevious();
  };

  const handleDownload = () => {
    const current = images[currentIndex];
    if (!current) return;
    const link = document.createElement('a');
    link.href = current.url;
    link.download = `${projectName}-${currentIndex + 1}.jpg`;
    link.click();
  };

  const current = images[currentIndex];
  if (!current) return null;

  return (
    <div className="fixed inset-0 z-9999 bg-stone-950/98 backdrop-blur-sm">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-linear-to-b from-black/60 to-transparent p-4 md:p-6">
        <div className="flex items-center justify-between mx-auto max-w-screen-2xl">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white/10 transition-colors"
              aria-label="Close gallery"
            >
              <X size={22} strokeWidth={1.5} />
            </button>
            <div className="hidden md:block">
              <p className="text-white font-sans text-sm uppercase tracking-widest">{projectName}</p>
              <p className="text-stone-400 text-xs font-sans mt-1">{currentIndex + 1} of {images.length}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <p className="md:hidden text-stone-300 text-sm font-sans">{currentIndex + 1} / {images.length}</p>
            <button
              onClick={() => setIsZoomed(!isZoomed)}
              className="p-2 text-white hover:bg-white/10 transition-colors"
              aria-label={isZoomed ? 'Zoom out' : 'Zoom in'}
            >
              {isZoomed ? <ZoomOut size={18} strokeWidth={1.5} /> : <ZoomIn size={18} strokeWidth={1.5} />}
            </button>
            <button
              onClick={handleDownload}
              className="hidden md:block p-2 text-white hover:bg-white/10 transition-colors"
              aria-label="Download image"
            >
              <Download size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Image */}
      <div
        className="absolute inset-0 flex items-center justify-center p-4 md:p-20"
        onTouchStart={onTouchStartHandler}
        onTouchMove={onTouchMoveHandler}
        onTouchEnd={onTouchEndHandler}
      >
        <div className={`relative w-full h-full transition-transform duration-300 ${isZoomed ? 'scale-150' : 'scale-100'}`}>
          <Image
            src={current.url}
            alt={current.alt || `${projectName} - Image ${currentIndex + 1}`}
            fill
            className="object-contain"
            sizes="100vw"
            priority
            quality={95}
          />
        </div>
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 text-white transition-all z-10"
            aria-label="Previous image"
          >
            <ChevronRight size={22} strokeWidth={1.5} className="rotate-180" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 text-white transition-all z-10"
            aria-label="Next image"
          >
            <ChevronRight size={22} strokeWidth={1.5} />
          </button>
        </>
      )}

      {/* Thumbnails */}
      <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/60 to-transparent p-4 md:p-6">
        <div className="mx-auto max-w-screen-2xl overflow-x-auto">
          <div className="flex gap-2 justify-center min-w-max px-4">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`relative shrink-0 w-16 h-16 md:w-20 md:h-20 overflow-hidden transition-all ${
                  index === currentIndex
                    ? 'ring-1 ring-white scale-110'
                    : 'opacity-40 hover:opacity-80'
                }`}
              >
                <Image
                  src={image.thumbnailUrl || image.url}
                  alt={`Thumbnail ${index + 1}`}
                  fill
                  className="object-cover"
                  sizes="80px"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StoneProjectDetailContent;
